import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  type ComboboxProps,
} from "@headlessui/react";
import { ClubPlayer } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { isPresent } from "@mixitone/util";
import PlayerDot from "components/common/PlayerDot";

interface Props {
  autoFocus?: boolean;
  canRegister?: boolean;
  onSelect(player: ClubPlayer): void;
  onRegister?(value: string): void;
  clubPlayers: ClubPlayer[];
  value: ClubPlayer | null | "new";
  className?: string;
}

interface State {
  canRegister: boolean;
  clubPlayers: ClubPlayer[];
  filteredClubPlayers: ClubPlayer[];
  search: string;
  onRegister(value: string): void;
  onSelect(player: ClubPlayer): void;
}

class PlayerSearchController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      clubPlayers: [],
      filteredClubPlayers: [],
      search: "",
      onRegister: () => {},
      onSelect: () => {},
      canRegister: true,
    };
  }

  async initialize(props: Props) {
    this.changeProps(props);
  }

  async changeProps(props: Props) {
    if (!props.clubPlayers) throw new Error("clubPlayers is required");
    this.state.clubPlayers = props.clubPlayers;
    if (props.onRegister) {
      this.state.onRegister = props.onRegister;
    }
    this.state.canRegister = props.canRegister ?? true;
    this.state.onSelect = props.onSelect;
  }

  actionSetSearch(search: string) {
    this.state.search = search;

    if (search.length < 3) {
      this.state.filteredClubPlayers = [];
      return;
    }

    const lowerCaseSearch = search.toLowerCase();

    this.state.filteredClubPlayers = this.state.clubPlayers.filter((player) => {
      const strings = [player.name, player.email, player.phone].filter(isPresent).join("").toLowerCase();
      return strings.includes(lowerCaseSearch);
    });
  }

  actionSelect(player: ClubPlayer) {
    this.state.onSelect(player);
    this.actionSetSearch("");
  }

  actionRegister() {
    if (this.state.search.length > 1) {
      this.state.onRegister(this.state.search);
    }
  }

  get canRegister() {
    return (
      this.state.canRegister &&
      this.state.search.length > 1 &&
      this.state.filteredClubPlayers.every(
        (player) => player.name!.toLowerCase() !== this.state.search.toLowerCase(),
      )
    );
  }
}

const PlayerSearch: React.FC<Props> = ({ value, autoFocus, className }) => {
  const controller = PlayerSearchController.use();
  const { filteredClubPlayers, search, canRegister } = controller.state;

  const options = filteredClubPlayers.map((player, idx) => {
    return (
      <ComboboxOption
        key={player.id}
        value={player}
        // biome-ignore lint/a11y/useValidAriaValues: old
        aria-roledescription=""
        className={clsx(
          "border-l border-r border-slate-300 p-4 ui-active:bg-brand-100 ui-active:text-black ui-not-active:bg-white ui-not-active:text-black",
          { "rounded-b-lg border-b": !canRegister && idx === filteredClubPlayers.length - 1 },
        )}
      >
        <PlayerDot player={player} inline />
      </ComboboxOption>
    );
  });

  // Trick to make the combobox accept the union type
  const comboboxProps: ComboboxProps<typeof value, false> = {
    value: value,
  };

  return (
    <Combobox
      {...comboboxProps}
      onChange={(v) => {
        if (v === "new") {
          controller.actionRegister();
        } else if (v instanceof ClubPlayer) {
          controller.actionSelect(v);
        }
      }}
    >
      <div className="relative w-full">
        <ComboboxInput
          title="Search for a player"
          data-testid="player-search-input"
          data-1p-ignore
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          placeholder="Register player"
          className={twMerge(
            "player-search-input mt-1 block w-full rounded border border-gray-300 p-2 bg-white",
            className,
          )}
          onChange={(event) => controller.actionSetSearch(event.target.value)}
          displayValue={(_value: ClubPlayer) => ""}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center pl-3">
          <svg
            aria-hidden="true"
            className="h-5 w-5 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      <ComboboxOptions
        anchor="bottom start"
        className="relative z-50"
        style={{ width: "var(--input-width)" }}
      >
        {options}
        {!canRegister && search.length > 1 && options.length === 0 && (
          <ComboboxOption
            value={""}
            key={"none"}
            className="rounded-b-lg border-b border-l border-r border-slate-300 p-4 text-center text-gray-500"
          >
            No players found
          </ComboboxOption>
        )}
        {canRegister && (
          <ComboboxOption
            key="new"
            value={"new"}
            disabled={!controller.canRegister}
            className="rounded-b-lg border-b border-l border-r border-slate-300 p-4 ui-active:bg-brand-100 ui-active:text-black ui-not-active:bg-white ui-not-active:text-black"
            onSelect={() => controller.actionRegister()}
          >
            Register {search}
          </ComboboxOption>
        )}
      </ComboboxOptions>
    </Combobox>
  );
};

const PlayerSearchScoped = PlayerSearchController.scope(ApplicationView(PlayerSearch));
export { PlayerSearchScoped as PlayerSearch };
